// Runtime configuration. The SPA is built ONCE (env-agnostic); a deployed env serves a
// `/config.js` (written by app-deploy) that sets `window.__APP_CONFIG__` BEFORE this
// module loads — index.html loads it ahead of the bundle — so the values below resolve
// to the deployed env synchronously at startup, with no async/ordering concerns and no
// stale module-load captures. Locally (vite dev) the committed `public/config.js` sets
// an empty object, so the Vite-env / localhost defaults below apply.
//
// The conciliatron API client uses `conciliatronApi` as its base (paths already include
// /api/v1). StorageHubApp is pointed at `storagehubApi` (must include /api/v1). `coreApi`
// is the core backend (membership/roles; must include /api/v1). Both backends are workspace-scoped
// (`/workspaces/{wsid}/...`) and read gateway claims; in the lean local stack there's no
// gateway, so `authzUserId` (actor) + `workspaceId` (the single workspace) are sent
// directly. `AUTH` feeds @micronet/auth's AuthProvider (its `config` prop).
import type { AuthConfig } from '@micronet/auth';

type RuntimeConfig = Partial<{
  conciliatronApi: string;
  storagehubApi: string;
  coreApi: string;
  workspaceId: number | string;
  authzUserId: string;
  azureClientId: string;
  azureTenantId: string;
  azureApiScope: string;
  devAuthBypass: boolean;
}>;

const rt: RuntimeConfig =
  (typeof window !== 'undefined' &&
    (window as unknown as { __APP_CONFIG__?: RuntimeConfig }).__APP_CONFIG__) ||
  {};

// The localhost fallbacks below are local-dev conveniences, but Vite bakes each
// one into the bundle as a string literal whether or not it is ever evaluated —
// which published the internal service topology (ports 8081/8082/8083) to anyone
// reading the JS. Gating them on `import.meta.env.DEV`, which Vite replaces with
// a literal `false` in a production build, drops them from the shipped bundle.
const devDefault = (url: string): string => (import.meta.env.DEV ? url : '');

const rawWorkspaceId = rt.workspaceId ?? import.meta.env.VITE_WORKSPACE_ID;

export const ENV = {
  conciliatronApi:
    rt.conciliatronApi ??
    import.meta.env.VITE_CONCILIATRON_API ??
    devDefault('http://localhost:8083'),
  storagehubApi:
    rt.storagehubApi ??
    import.meta.env.VITE_STORAGEHUB_API ??
    devDefault('http://localhost:8082/api/v1'),
  coreApi:
    rt.coreApi ?? import.meta.env.VITE_CORE_API ?? devDefault('http://localhost:8081/api/v1'),
  // No fallback on purpose. This used to default to 1, and a deployed config.js
  // writes no workspaceId at all (app-deploy.yml emits only the API bases and the
  // Entra keys), so every user in every environment booted into workspace 1 — which
  // in geoinnova-dev is archived. The starting workspace is a server-side fact; the
  // store asks /me for it (see useStore.init). Undefined here means "unseeded", and
  // only local dev, where docker-compose sets VITE_WORKSPACE_ID, ever seeds it.
  workspaceId: rawWorkspaceId === undefined || rawWorkspaceId === '' ? undefined : Number(rawWorkspaceId),
  authzUserId: rt.authzUserId ?? import.meta.env.VITE_AUTHZ_USER_ID ?? '1',
} as const;

// With the dev defaults gone, an unset base in a deployed build would otherwise
// resolve to '' and send every call to a relative URL — which the SPA's own
// CloudFront answers with index.html, so failures surface as unparseable HTML
// far from the cause. Fail at startup instead, naming what the deploy omitted.
// In a dev build the defaults above always fill in, so this never fires locally.
const missingBases = (['conciliatronApi', 'storagehubApi', 'coreApi'] as const).filter(
  (key) => !ENV[key]
);
if (missingBases.length > 0) {
  throw new Error(
    `Missing API base URL(s): ${missingBases.join(', ')}. The deploy must serve a ` +
      '/config.js that sets window.__APP_CONFIG__ with every base before the bundle loads.'
  );
}

// Entra/auth config. Runtime (window.__APP_CONFIG__) wins; else the build-time
// VITE_AZURE_* / VITE_DEV_AUTH_BYPASS for local dev. Passed to AuthProvider's `config`.
export const AUTH: AuthConfig = {
  clientId: rt.azureClientId ?? (import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined),
  tenantId: rt.azureTenantId ?? (import.meta.env.VITE_AZURE_TENANT_ID as string | undefined),
  apiScope: rt.azureApiScope ?? (import.meta.env.VITE_AZURE_API_SCOPE as string | undefined),
  devBypass: rt.devAuthBypass ?? import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',
};
